import React from 'react';
import Draggable, {
  type DraggableEventHandler,
  type DraggableProps,
} from 'react-draggable';
import {useIsomorphicLayoutEffect} from '@packages/systems/spring-hooks';

import {HEADER_HANDLE, mergeCancelSelectors} from './_constants';

export type ModalDraggableProps = Partial<
  Omit<DraggableProps, 'bounds' | 'handle' | 'offsetParent'>
>;

type ModalDraggableWrapperProps = {
  children: React.ReactNode;
  isDragDisabled?: boolean;
  isFullScreen?: boolean;
  /**
   * A ref to the modal element.
   */
  modalRef?: React.RefObject<HTMLDivElement>;
} & ModalDraggableProps;

type ViewportBounds = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

type DragOffset = {x: number; y: number};

/**
 * Bounds constrain the draggable's x,y (the translate offset).
 * rect = basePosition + (x, y), so basePosition = rect - (x, y).
 * We need bounds such that basePosition + (x, y) stays in the viewport.
 */
function getViewportBounds(
  node: HTMLElement,
  offset: DragOffset
): ViewportBounds {
  const rect = node.getBoundingClientRect();
  const {x, y} = offset;
  const baseLeft = rect.left - x;
  const baseTop = rect.top - y;
  const baseRight = rect.right - x;
  const baseBottom = rect.bottom - y;
  return {
    left: -baseLeft,
    top: -baseTop,
    right: window.innerWidth - baseRight,
    bottom: window.innerHeight - baseBottom,
  };
}

export const ModalDraggableWrapper = ({
  children,
  cancel,
  isFullScreen,
  isDragDisabled,
  modalRef,
  onStart,
  onStop,
  ...props
}: ModalDraggableWrapperProps) => {
  const [isDragging, setDragging] = React.useState(false);
  /**
   * Manually compute bounds in viewport space because react-draggable's
   * bounds="body" mixes offsetLeft with body dimensions, causing incorrect
   * constraints. We use getBoundingClientRect() + offsetParent=body instead.
   */
  const [bounds, setBounds] = React.useState<ViewportBounds | null>(null);
  const [position, _setPosition] = React.useState<DragOffset>({x: 0, y: 0});

  /**
   * We are using a ref here to pass the most up to date info to our resize observer.
   * By using a ref, we avoid having to unmount & remount the resize observer when the position changes.
   */
  const positionRef = React.useRef<DragOffset>(position);
  const savedPositionRef = React.useRef<DragOffset>({x: 0, y: 0});

  const isDraggingRef = React.useRef(false);
  isDraggingRef.current = isDragging;
  const resizeTimeoutIdRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  /**
   * Keep the ref synced with postion, but do not wait for the state update.
   */
  const setPosition = (newPosition: DragOffset) => {
    positionRef.current = newPosition;
    _setPosition(newPosition);
  };

  useIsomorphicLayoutEffect(() => {
    if (isFullScreen) {
      /**
       * We save our position so we can restore it when we exit fullscreen.
       * Setting position to 0 here will return our modal to its original position, so resizing works correctly.
       */
      savedPositionRef.current = {...positionRef.current};
      setPosition({x: 0, y: 0});
    } else {
      /**
       * Restore before paint to avoid a flash of the modal at (0,0).
       */
      setPosition(savedPositionRef.current);
    }
  }, [isFullScreen]);

  useIsomorphicLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const modal = modalRef?.current;
    if (!modal || !(modal instanceof HTMLElement)) return;

    const updateBounds = () => {
      if (isDraggingRef.current) return;
      setBounds(getViewportBounds(modal, positionRef.current));
    };

    const debouncedUpdateBounds = () => {
      if (resizeTimeoutIdRef.current !== null)
        clearTimeout(resizeTimeoutIdRef.current);
      resizeTimeoutIdRef.current = setTimeout(() => {
        requestAnimationFrame(() => {
          if (isDraggingRef.current) return;
          updateBounds();
        });
      }, 150);
    };

    if (bounds !== null) {
      updateBounds();
    }

    const resizeObserver = new ResizeObserver(debouncedUpdateBounds);
    resizeObserver.observe(modal);

    window.addEventListener('resize', debouncedUpdateBounds);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', debouncedUpdateBounds);
      if (resizeTimeoutIdRef.current !== null)
        clearTimeout(resizeTimeoutIdRef.current);
    };
  }, []);

  const mergedCancel = React.useMemo(
    () => mergeCancelSelectors(cancel),
    [cancel]
  );

  const handleStart = React.useCallback<DraggableEventHandler>(
    (e, data) => {
      setDragging(true);
      onStart?.(e, data);
    },
    [onStart]
  );

  const handleStop = React.useCallback<DraggableEventHandler>(
    (e, data) => {
      setDragging(false);
      setPosition({x: data.x, y: data.y});
      savedPositionRef.current = {x: data.x, y: data.y};
      onStop?.(e, data);
    },
    [onStop]
  );

  return (
    <Draggable
      {...props}
      bounds={bounds ?? false}
      cancel={mergedCancel}
      disabled={isFullScreen || isDragDisabled === true}
      handle={HEADER_HANDLE}
      nodeRef={modalRef}
      offsetParent={typeof document !== 'undefined' ? document.body : undefined}
      onStart={handleStart}
      onStop={handleStop}
      position={position}
    >
      {children}
    </Draggable>
  );
};

ModalDraggableWrapper.displayName = 'ModalDraggableWrapper';
